import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '@/contexts/AdminContext';
import { useTranslation } from 'react-i18next';
import { Lock, User, Wrench } from 'lucide-react';

const AdminLogin = () => {
  const { t, i18n } = useTranslation();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAdmin();
  const navigate = useNavigate();

  const languages = [
    { code: 'it', name: 'IT', flag: 'https://flagcdn.com/w40/it.png' },
    { code: 'en', name: 'EN', flag: 'https://flagcdn.com/w40/gb.png' },
    { code: 'de', name: 'DE', flag: 'https://flagcdn.com/w40/de.png' },
    { code: 'fr', name: 'FR', flag: 'https://flagcdn.com/w40/fr.png' },
    { code: 'es', name: 'ES', flag: 'https://flagcdn.com/w40/es.png' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    try {
      const result = await login(password);
      if (result.success) {
        // Redirect in base al ruolo
        if (result.role === 'tech') {
          navigate('/admin/statistics');
        } else {
          navigate('/admin/dashboard');
        }
      } else {
        setError(result.error || t('admin.login.error'));
      }
    } catch (err) {
      setError(t('admin.login.connectionError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center px-4">
      {/* Language Selector - Top Right */}
      <div className="absolute top-4 right-4 flex items-center gap-1 bg-white/20 backdrop-blur rounded-lg p-1">
        {languages.map((lang) => (
          <button
            key={lang.code}
            onClick={() => i18n.changeLanguage(lang.code)}
            title={lang.name}
            className={`p-1 rounded transition-all ${
              i18n.language === lang.code
                ? 'ring-2 ring-white scale-110 bg-white/30'
                : 'opacity-70 hover:opacity-100 hover:scale-105'
            }`}
          >
            <img 
              src={lang.flag} 
              alt={lang.name}
              className="w-6 h-4 object-cover rounded-sm"
            />
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <Lock className="text-blue-600" size={32} />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('admin.login.title')}</h1>
          <p className="text-gray-600">{t('admin.login.subtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="admin-login-form">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('admin.login.password')}
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('admin.login.placeholder')}
              required
              autoFocus
              disabled={isLoading}
              data-testid="password-input"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg" data-testid="login-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="login-submit"
          >
            {isLoading ? t('admin.login.loading') : t('admin.login.submit')}
          </button>
        </form>

        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center mb-3">{t('admin.login.accessTypes')}</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center text-xs text-gray-600 bg-gray-50 p-2 rounded">
              <User size={14} className="mr-2 text-blue-600" />
              <span>{t('admin.login.adminAccess')}</span>
            </div>
            <div className="flex items-center text-xs text-gray-600 bg-gray-50 p-2 rounded">
              <Wrench size={14} className="mr-2 text-emerald-600" />
              <span>{t('admin.login.techAccess')}</span>
            </div>
          </div>
        </div>

        <div className="mt-4 text-center text-sm text-gray-600">
          <p>{t('admin.login.forgotPassword')}</p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;